import { useCallback, useEffect, useRef, useState } from "react";

import { useElationBridge } from "~/bridge/useElationBridge";
import type { ElationPatient } from "~/bridge/types";

/**
 * SWITCH candidate for an ANCHORED view — a member profile opened from an EHR
 * match, which carries the matched Elation patient id as its anchor. Stay put
 * unless a *genuinely different*, non-null, non-suppressed patient is detected.
 * The `anchor != null` guard means this only applies to anchored views; manual
 * (unanchored) views use `isForegroundFollow` instead.
 *
 * `isActive` is the keep-alive-shell gate: TabbedAppShell mounts every open
 * profile at once (hidden via CSS), so a hidden tab must NEVER emit a candidate
 * — otherwise it fires navigate() off the shared Elation bridge and yanks the
 * shell out from under the tab the user is actually viewing. Only the active
 * (visible) tab drives navigation. Defaults conceptually to "active" for the
 * non-shell path, where the view is only mounted when it IS the active view.
 */
export function isSwitchCandidate(
  isActive: boolean,
  anchor: string | null,
  liveId: string | null,
  suppressed: string | null,
): boolean {
  return (
    isActive &&
    anchor != null &&
    liveId != null &&
    liveId !== anchor &&
    liveId !== suppressed
  );
}

/**
 * FOLLOW candidate for an UNANCHORED view — a member profile opened by a manual
 * search (no `ehrPatientId` carried), so the view isn't tied to any chart. Here
 * we follow the FOREGROUND Elation chart: while a chart is the foreground tab
 * (`sourceTabActive`) and shows a non-suppressed patient, that patient is a
 * candidate to navigate to.
 *
 * `suppressed` is seeded by the hook with the patient already foreground when
 * the view opened, so the member the user just actively opened is respected
 * (no bounce). A chart that is BACKGROUNDED at open is not suppressed, so
 * bringing it to the foreground afterwards follows it to that patient — the
 * "return to the Elation chart re-navigates" behavior. The `sourceTabActive`
 * gate keeps a background chart from ever yanking the view.
 *
 * `isActive` is the keep-alive-shell gate (see `isSwitchCandidate`): a hidden
 * manual tab must not follow the foreground chart, or every open manual profile
 * races to navigate on the same shared bridge data.
 */
export function isForegroundFollow(
  isActive: boolean,
  sourceTabActive: boolean,
  liveId: string | null,
  suppressed: string | null,
): boolean {
  return (
    isActive && sourceTabActive === true && liveId != null && liveId !== suppressed
  );
}

/**
 * Behavior for a member-profile view when a different Elation chart is detected.
 * Anchored (EHR-match) views switch on a genuinely different patient; unanchored
 * (manually-opened) views follow the foreground chart. `candidate` clears
 * automatically when the detection reverts/empties, so a prompt hides itself if
 * the user switches the chart back before choosing.
 *
 * @param anchorEhrPatientId the Elation patient id this view is anchored to
 *   (passed via router state from a match). Absent (null) for a manual search
 *   result — that view follows the foreground chart instead of anchoring.
 * @param isActive whether this profile view is the ACTIVE/visible tab. Under the
 *   keep-alive multi-tab shell (VITE_FEATURE_STATEFUL_TAB_SHELL) every open
 *   profile stays mounted at once, so a hidden tab must not emit a switch
 *   candidate (which would fire navigate() and yank the shell). Defaults true —
 *   in the non-shell path the view is only mounted when it IS the active view.
 */
export function useEhrPatientSwitch(
  anchorEhrPatientId: string | null,
  isActive = true,
): {
  candidate: ElationPatient | null;
  stay: () => void;
} {
  const { data, sourceTabActive } = useElationBridge();
  const liveId = data?.ehrPatientId ?? null;

  const [anchor] = useState<string | null>(anchorEhrPatientId);
  const [suppressed, setSuppressed] = useState<string | null>(null);

  // Unanchored (manual) views only: seed `suppressed` with the patient that is
  // ALREADY foreground when the first chart is detected, so we don't bounce the
  // member the user just opened. Keyed on the first non-null patient, whose
  // delivery carries the settled `sourceTabActive` (they're posted together), so
  // this reads the real open-time foreground state, not the bridge's mount-time
  // re-init. A backgrounded-at-open chart is intentionally NOT seeded → focusing
  // it later follows it.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || liveId == null) return;
    seededRef.current = true;
    if (anchor == null && sourceTabActive) setSuppressed(liveId);
  }, [anchor, liveId, sourceTabActive]);

  const isCandidate =
    anchor != null
      ? isSwitchCandidate(isActive, anchor, liveId, suppressed)
      : isForegroundFollow(isActive, sourceTabActive, liveId, suppressed);
  const candidate = isCandidate ? data : null;

  // "Stay": suppress this patient so the prompt/nav doesn't immediately reappear;
  // a later switch to yet another patient still prompts.
  const stay = useCallback(() => {
    if (liveId != null) setSuppressed(liveId);
  }, [liveId]);

  return { candidate, stay };
}
